import {
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import React, { useState, useEffect } from "react"; // Re-added useEffect
import { useNavigate } from "react-router-dom";
import { ErrorMessage, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import queryGraphql from "../../helpers/ApiInterface"; // Re-added ApiInterface
import MiniBot from "../MiniBot";
import MainDrawer from "../MainDrawer";
import { useAuth } from "../../helpers/AuthProvider";

// --- Interfaces and Constants ---
interface APIFormProps {
    isAddExisting: boolean;
}

const validationSchema = Yup.object().shape({
    applicationName: Yup.string().required("Application is required"),
    microservice: Yup.string().required("Microservice is required"),
    name: Yup.string()
        .required("API Backend Name is required"),
    codeTemplate: Yup.string().required("Code Template is required"),
    path: Yup.string()
    .required("Endpoint Path is required")
    .lowercase("Path must be in lowercase.") 
    .matches(
        /^[a-z0-9_-]+$/, 
        "Path must only contain lowercase letters, numbers, hyphens, and underscores."
    ),
    httpMethod: Yup.string().required("HTTP Method is required"),
    apiVersion: Yup.string().required("API Version is required"),
    description: Yup.string().required("Description is required"),
    mockRequest: Yup.string().test('is-json', 'Mock Request must be a valid JSON format', (value: string | undefined) => {
        if (!value) return true;
        try { JSON.parse(value); } catch (e) { return false; }
        return true;
    }),
    mockResponse: Yup.string().test('is-json', 'Mock Response must be a valid JSON format', (value: string | undefined) => {
        if (!value) return true;
        try { JSON.parse(value); } catch (e) { return false; }
        return true;
    }),
     costCenterValue: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(20, "This field must not exceed 20 characters"),

    costLabelPairs: Yup.array()
        .of(
            Yup.object({
                cost: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
                label: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
            })
        ),
    swaggerYaml: Yup.string().required("Swagger YAML configuration is required"),
});

const httpMethodList = ["GET", "POST", "PUT", "DELETE", "PATCH"];

// --- Form Content Component ---
const AddExistingAPIFormContent: React.FC<APIFormProps> = ({ isAddExisting }) => {
    const navigate = useNavigate();
    const CREATE_NEW_SERVICE_OPTION = "--- Create New Service ---";
    
    const [applicationsWithServices, setApplicationsWithServices] = useState<any[]>([]);
    const [servicesForSelectedApp, setServicesForSelectedApp] = useState<any[]>([]);

    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    // RESTORED: API call to fetch applications and services for dropdowns
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const data = await queryGraphql(`
                    query Applications {
                      applications { id, applicationName, services { id, serviceName, framework } cluster { cloudProvider } }
                    }`);
                if (data.applications) setApplicationsWithServices(data.applications);
            } catch (error) { console.error("Error fetching applications:", error); }
        };
        fetchDropdownData();
    }, []);

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/apis">APIs Backend</Link>
                    <Typography color="text.primary">Add Existing API</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/apis")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Add Existing API</Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                    <Formik
                        initialValues={{
                            applicationName: "",
                            microservice: "",
                            name: "",
                            codeTemplate: "",
                            path: "",
                            httpMethod: "",
                            apiVersion: "",
                            description: "",
                            mockRequest: "",
                            mockResponse: "",
                            costCenterValue: "",
                            costLabelPairs: [],
                            swaggerYaml: "",
                        }}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={async (values) => {
                            // Submission API call is ignored, we only simulate it.
                            console.log("FORM SUBMITTED FOR EXISTING API:", values);
                            alert("Existing API Added! Check the console for the form data.");
                            navigate("/apis");
                        }}
                    >
                        {({ setFieldValue, errors, touched, values, handleChange, handleBlur, isValid, dirty }) => (
                            <Form id="main-form">
                                <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
                                    Fill the below details to add an existing API
                                </Typography>

                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="API Backend Name" />
                                            <TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Display Name of API (e.g. GetPetName)" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.name && touched.name ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Application Name" />
                                            <Autocomplete options={applicationsWithServices.map(app => app.applicationName)}
                                                onChange={(_, newValue) => {
                                                    setFieldValue("applicationName", newValue || "");
                                                    setFieldValue("microservice", "");
                                                    const appData = applicationsWithServices.find(app => app.applicationName === newValue);
                                                    setServicesForSelectedApp(appData ? appData.services : []);
                                                }}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select the application" hiddenLabel 
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.applicationName && touched.applicationName ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="applicationName" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Service Name" />
                                            <Autocomplete
                                                options={
                                                    values.applicationName
                                                        ? [...servicesForSelectedApp.map(s => s.serviceName), CREATE_NEW_SERVICE_OPTION]
                                                        : servicesForSelectedApp.map(s => s.serviceName)
                                                }
                                                value={values.microservice}
                                                onChange={(_, newValue) => {
                                                    if (newValue === CREATE_NEW_SERVICE_OPTION) {
                                                        navigate("/services/add");
                                                    } else {
                                                        setFieldValue("microservice", newValue || "");
                                                        const selectedService = servicesForSelectedApp.find(s => s.serviceName === newValue);
                                                        setFieldValue("codeTemplate", selectedService?.framework || "");
                                                    }
                                                }}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select Service Name" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.microservice && touched.microservice ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="microservice" />
                                            </div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Code Template" />
                                                <TextField
                                                    variant="filled"
                                                    fullWidth
                                                    disabled
                                                    name="codeTemplate"
                                                    value={values.codeTemplate}
                                                    hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": {
                                                        border: errors.codeTemplate && touched.codeTemplate ? "1px solid red" : "1px solid #00000000",
                                                        backgroundColor: "#f0f0f0"
                                                    }}}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
                                                />
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                    <ErrorMessage name="codeTemplate" />
                                                </div>
                                            </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Path" />
                                            <TextField variant="filled" fullWidth name="path" value={values.path} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Endpoint Path" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.path && touched.path ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="path" /></div>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="HTTP Method" />
                                            <Autocomplete options={httpMethodList}
                                                onChange={(_, newValue) => setFieldValue("httpMethod", newValue || "")}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select HTTP Method" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.httpMethod && touched.httpMethod ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="httpMethod" /></div>
                                        </Grid>
                                        
                                        <Grid item xs={6}>
                                            <CompulsoryFieldsLabel fieldName="API Version" />
                                            <TextField variant="filled" fullWidth name="apiVersion" value={values.apiVersion} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., v1, v2" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.apiVersion && touched.apiVersion ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="apiVersion" /></div>
                                        </Grid>
                                    </Grid>
                                </div>
                                    
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        API Configuration
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12}>
                                            <CompulsoryFieldsLabel fieldName="Description" />
                                            <TextField variant="filled" fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Please describe the function of your API" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.description && touched.description ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography fontFamily="Gilroy-Regular" fontSize="14px" mb={1}>Mock Request</Typography>
                                            <TextField variant="filled" fullWidth multiline rows={4} name="mockRequest" value={values.mockRequest} onChange={handleChange} onBlur={handleBlur} placeholder="Please enter the mock request in a valid JSON format" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.mockRequest && touched.mockRequest ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="mockRequest" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography fontFamily="Gilroy-Regular" fontSize="14px" mb={1}>Mock Response</Typography>
                                            <TextField variant="filled" fullWidth multiline rows={4} name="mockResponse" value={values.mockResponse} onChange={handleChange} onBlur={handleBlur} placeholder="Please enter the mock response in a valid JSON format" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.mockResponse && touched.mockResponse ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="mockResponse" /></div>
                                        </Grid>
                                    </Grid>
                                </div>
                                 
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        API Tags / Labels
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Value</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth disabled value="cost-center" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth placeholder="Enter value" value={values.costCenterValue} hiddenLabel variant="filled" name="costCenterValue"
                                                sx={{ "& .MuiFilledInput-root": { border: errors.costCenterValue && touched.costCenterValue ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                onChange={handleChange} onBlur={handleBlur}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" component="div" /></div>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={4} mt="8px">
                                        {values.costLabelPairs?.map((pair:any, index:any) => (
                                            <React.Fragment key={pair.id || index}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth placeholder="Enter key" value={pair.cost} hiddenLabel variant="filled"
                                                        sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                        onChange={(event) => setFieldValue(`costLabelPairs[${index}].cost`, event.target.value)}
                                                        InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].cost`} component="div" /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box display="flex" alignItems="center">
                                                        <TextField fullWidth placeholder="Enter value" value={pair.label} hiddenLabel variant="filled"
                                                            sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                            onChange={(event) => setFieldValue(`costLabelPairs[${index}].label`, event.target.value)}
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                        <IconButton sx={{ marginLeft: "8px" }} onClick={() => {
                                                            const newPairs = values.costLabelPairs.filter((_:any, i:any) => i !== index);
                                                            setFieldValue("costLabelPairs", newPairs);
                                                        }}>
                                                            <img alt="close-icon" src="/assets/close-icon.svg" />
                                                        </IconButton>
                                                    </Box>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].label`} component="div" /></div>
                                                </Grid>
                                            </React.Fragment>
                                        ))}
                                    </Grid>
                                    <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }])}>
                                        + Add Key Value Pair
                                    </Button>
                                </div>
                                
                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        Swagger YAML Configuration
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <TextField
                                        name="swaggerYaml"
                                        variant="filled"
                                        multiline
                                        fullWidth
                                        rows={15}
                                        value={values.swaggerYaml}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter or paste Swagger/OpenAPI YAML definition here."
                                        sx={{
                                            "& .MuiFilledInput-root": {
                                                border: errors.swaggerYaml && touched.swaggerYaml ? "1px solid red" : "1px solid #e0e0e0",
                                                "&.Mui-focused": {
                                                    backgroundColor: "#f5f5f5",
                                                    border: "1px solid black",
                                                },
                                                backgroundColor: "#fafafa",
                                            },
                                            "& textarea": {
                                                fontFamily: "monospace",
                                                fontSize: "0.875rem",
                                                lineHeight: "1.5",
                                            }
                                        }}
                                        InputProps={{ disableUnderline: true, style: { borderRadius: "8px", padding: "16px" } }}
                                    />
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                        <ErrorMessage name="swaggerYaml" />
                                    </div>
                                </div>

                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/apis")}>Cancel</Button>
                                    <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!isValid || !dirty || isViewer }>
                                        Add API Backend
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

// --- Main Export Component ---
const AddExistingAPIForm: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-backend"} />
            <Box component="main" sx={{ flexGrow: 1 }}>
                <AddExistingAPIFormContent isAddExisting={true} />
            </Box>
        </Box>
    );
};

export default AddExistingAPIForm;